import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button } from "react-bootstrap";
import DataGrid, {
    Column,
    Scrolling,
    Selection
} from "devextreme-react/cjs/data-grid";
import { decryptItem } from "helpers/secureNavigate";
import { assignProduct, getMakeToOrderItems } from "../services/ecomSalesOrders.service";
import Loader from "Common/Loader";
import { HttpStatus } from "modules/core";
import notistack from "modules/core/notistack";

const AssignProduct: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate()
    const [data, setData] = useState<any[][]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<{ [key: number]: number[] }>({});
    const [selectedItems, setSelectedItems] = useState<{ [key: number]: any }>({});

    const fetchData = async () => {
        try {
            setLoading(true);
            if (!id) return;

            const salesOrderId: string | number = decryptItem(id) ?? 0;
            const res = await getMakeToOrderItems({ salesOrderId });

            setData(res?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSelectionChanged = (groupIndex: number, e: any, groupData: any[]) => {
        const selectedKey = e.selectedRowKeys[0];

        const selectedItem = groupData.find(x => x.id === selectedKey);

        setSelectedRowKeys(prev => ({
            ...prev,
            [groupIndex]: selectedKey ? [selectedKey] : []
        }));

        setSelectedItems(prev => ({
            ...prev,
            [groupIndex]: selectedItem
        }));
    };

    const [submitting, setSubmitting] = useState(false)
    const handleSubmit = async () => {
        try {
            if (!id) return;

            const salesOrderId: string | number = decryptItem(id) ?? 0;
            const selectedValues = Object.values(selectedItems);

            if (selectedValues.length === 0) {
                notistack.warning("Please select at least one product");
                return;
            }

            const ids = selectedValues.map((item: any) => item?.id);
            const hasDuplicate = new Set(ids).size !== ids.length;

            if (hasDuplicate) {
                notistack.warning("Same product cannot be selected multiple times");
                return;
            }

            const makeTOOrderProducts = selectedValues.map((item: any) => ({
                productHeaderId: Number(item?.id ?? 0),
                customOrderItemID: Number(item?.customOrderItemId ?? 0)
            }));

            const payload = {
                salesOrderId: Number(salesOrderId),
                makeTOOrderProducts
            };

            setSubmitting(true);

            const response = await assignProduct(payload);

            if (response.status === HttpStatus.Success) {
                navigate(-1);
                notistack.notifyFromApi(response);
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-content container-fluid">
            <Card className="mt-3">
                <Card.Header>
                    <h5>Assign Product</h5>
                </Card.Header>

                <Card.Body>

                    {loading && <Loader />}

                    {!loading && data?.map((group: any[], groupIndex: number) => (
                        <Card key={groupIndex} className="mb-3">
                            <Card.Header>
                                <b>Make to Order Product {groupIndex + 1}</b>
                            </Card.Header>

                            <Card.Body>

                                <DataGrid
                                    dataSource={group}
                                    keyExpr="id"
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    selectedRowKeys={selectedRowKeys[groupIndex] || []}
                                    onSelectionChanged={(e) =>
                                        handleSelectionChanged(groupIndex, e, group)
                                    }
                                >
                                    <Selection
                                        mode="multiple"
                                        showCheckBoxesMode="always"
                                    />

                                    <Scrolling
                                        mode="standard"
                                        useNative={true}
                                        scrollByContent={true}
                                        scrollByThumb={true}
                                    />
                                    <Column dataField="invoiceNo" caption="Invoice No." />
                                    <Column
                                        dataField="invoiceDate"
                                        caption="Invoice Date"
                                        dataType="date"
                                        format="dd/MM/yyyy"
                                    />
                                    <Column dataField="styleNo" caption="Style No." />
                                    <Column dataField="metalKT" caption="Kt" />
                                    <Column dataField="metalColor" caption="Metal Color" />

                                    <Column dataField="grossWt" caption="Gross Wt" format="#,##0.000" />
                                    <Column dataField="netWt" caption="Net Wt" format="#,##0.000" />
                                    <Column dataField="diaWt" caption="Dia Wt" format="#,##0.000" />

                                    <Column dataField="diaQty" caption="Dia Qty" />
                                    <Column dataField="qty" caption="Qty" />

                                    <Column dataField="mrpPrice" caption="MRP" format="#,##0.00" />
                                    <Column dataField="dpPrice" caption="DP Price" format="#,##0.00" />

                                    <Column dataField="huid" caption="HUID" />
                                    <Column dataField="bagNo" caption="Bag No." />
                                    <Column dataField="storeCode" caption="Store Code" />

                                    <Column dataField="isSRP" caption="Is SRP" dataType="boolean"
                                        cellRender={({ data }) => (
                                            <span>{data.isSRP ? "SRP" : "MRP"}</span>
                                        )}
                                    />

                                    <Column dataField="colorStoneWt" caption="Color Stone Wt" format="#,##0.000" />
                                    <Column dataField="otherWt" caption="Other Wt" format="#,##0.000" />
                                </DataGrid>

                            </Card.Body>
                        </Card>
                    ))}

                    <div className="d-flex align-items-center justify-content-end mt-2 gap-2">
                        <Button
                            onClick={() => {
                                navigate(-1)
                            }}
                            disabled={submitting}
                            className="btn add-btn"
                        >
                            back
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="btn submit-btn"
                        >
                            Save
                        </Button>
                    </div>

                </Card.Body>
            </Card>
        </div>
    );
};

export default AssignProduct;